// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 63.23.0 decision 8 — the OAuth consent screen, `/oauth/consent`.
 *
 * `GET /oauth/authorize` validates the client, redirect, resource, scope and
 * PKCE challenge, then sends the browser here with `?request=<id>`. The page
 * sits OUTSIDE the admin shell: it is a single decision, not a workspace.
 *
 * It requires a normal session (`ConsentSessionGate`): a signed-out visitor
 * is sent through the existing login with `?next=` naming this exact URL, so
 * SSO and MFA apply unchanged and the person comes straight back.
 *
 * What it shows (decision 8): the client's name and redirect host, the
 * organisation, the scopes in plain language with the tools they reach, and
 * whether the client registered itself or an administrator registered it.
 *
 * Allow and Deny are a real `<button>` pair. Both disable on the FIRST press
 * of either — a double submit would answer a single-use request twice — and
 * stay disabled until the server answers. The server returns where to go
 * (`redirect_to`: the client's redirect URI with a code, or with
 * `error=access_denied`); the page never builds that URL itself. The POST
 * carries the CSRF token like every other mutation through `apiClient`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocale } from '../../hooks/useLocale.js';
import { usePageTitle } from '../../hooks/usePageTitle.js';
import type { McpOAuthConsentDetails } from '@vn-grc/shared';
import { apiClient } from '../../services/api-client.js';
import { extractErrorMessage, serverCodeOf } from '../../utils/api-helpers.js';
import { formatTemplate } from '../../utils/i18n-helpers.js';
import { ConsentSessionGate } from './ConsentSessionGate.js';

type LoadState =
  | { status: 'loading' }
  | { status: 'failed'; message: string }
  | { status: 'ready'; request: McpOAuthConsentDetails };

const containerStyle: React.CSSProperties = { maxWidth: '560px' };
const listStyle: React.CSSProperties = {
  margin: '0 0 var(--space-md, 1rem)',
  paddingLeft: '1.25rem',
};

/*
 * Gate E12.5 pass 1 (P2-8): the consent routes refuse in English prose. The
 * codes they refuse with are read here and shown in the page's own copy;
 * anything else keeps the server's sentence or the fallback.
 */
function refusalText(
  err: unknown,
  fallback: string,
  c: {
    error_request_not_found: string;
    error_request_already_decided: string;
    error_client_revoked: string;
  },
): string {
  switch (serverCodeOf(err)) {
    case 'oauth_request_not_found':
      return c.error_request_not_found;
    case 'oauth_request_already_decided':
      return c.error_request_already_decided;
    case 'oauth_client_revoked':
      return c.error_client_revoked;
    default:
      return extractErrorMessage(err, fallback);
  }
}

export function ConsentPage(): JSX.Element {
  return (
    <ConsentSessionGate>
      <ConsentDecision />
    </ConsentSessionGate>
  );
}

/** The request and the decision. Rendered only inside a usable session. */
function ConsentDecision(): JSX.Element {
  const { t } = useLocale();
  const c = t.oauth_consent;
  usePageTitle(c.page_title);
  const [params] = useSearchParams();
  const requestId = params.get('request');

  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [submitting, setSubmitting] = useState<'approve' | 'deny' | null>(null);
  const [decided, setDecided] = useState(false);
  const [decideError, setDecideError] = useState<string | null>(null);
  // Gate E12.5 pass 1 (P2-3): the re-entry guard is a ref, set in the same
  // tick as the first call. State is read through the closure of the render
  // that bound the handler, so two calls before React re-renders both saw
  // `submitting === null` and both POSTed.
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (!requestId) return;
    setState({ status: 'loading' });
    try {
      const request = await apiClient.mcpConsentGet(requestId);
      if (!request || typeof request.client_name !== 'string') {
        setState({ status: 'failed', message: c.error_load });
        return;
      }
      setState({
        status: 'ready',
        request: {
          ...request,
          scopes: Array.isArray(request.scopes) ? request.scopes : [],
          tools: Array.isArray(request.tools) ? request.tools : [],
          // An unknown or absent value shows no warning text it cannot name;
          // the two known values are the only ones a sentence exists for.
          redirect_warning:
            request.redirect_warning === 'loopback' ||
            request.redirect_warning === 'private_use_scheme'
              ? request.redirect_warning
              : null,
        },
      });
    } catch (err) {
      setState({ status: 'failed', message: refusalText(err, c.error_load, c) });
    }
  }, [requestId, c]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(decision: 'approve' | 'deny'): Promise<void> {
    if (!requestId || inFlight.current) return;
    inFlight.current = true;
    setSubmitting(decision);
    setDecideError(null);
    try {
      const res = await apiClient.mcpConsentDecide(requestId, decision);
      if (!res?.redirect_to) throw new Error(c.error_decide);
      setDecided(true);
      window.location.assign(res.redirect_to);
    } catch (err) {
      setDecideError(refusalText(err, c.error_decide, c));
      setSubmitting(null);
      inFlight.current = false;
    }
  }

  const request = state.status === 'ready' ? state.request : null;
  const heading = request
    ? formatTemplate(c.title, { client: request.client_name })
    : c.title_pending;
  const locked = submitting !== null || decided;

  const scopeText = (scope: string) =>
    scope === 'mcp:read'
      ? c.scope_read
      : scope === 'mcp:write'
        ? c.scope_write
        : formatTemplate(c.scope_other, { scope });

  return (
    <main className="login-page">
      <div className="login-container" style={containerStyle} data-testid="oauth-consent">
        <h1>{heading}</h1>

        {!requestId ? (
          <p className="login-error" role="alert" data-testid="oauth-consent-missing">
            {c.missing_request}
          </p>
        ) : state.status === 'loading' ? (
          <p role="status">{c.loading}</p>
        ) : state.status === 'failed' ? (
          <div className="login-error" role="alert" data-testid="oauth-consent-load-error">
            <p>{state.message}</p>
            <button type="button" className="btn btn-secondary" onClick={() => void load()}>
              {c.reload}
            </button>
          </div>
        ) : (
          request && (
            <>
              <p className="login-subtitle">
                {formatTemplate(c.intro, { client: request.client_name })}
              </p>
              <dl className="dm-definition-list" data-testid="oauth-consent-facts">
                <dt>{c.client_label}</dt>
                <dd data-testid="oauth-consent-client">{request.client_name}</dd>
                <dt>{c.origin_label}</dt>
                <dd data-testid="oauth-consent-origin">
                  {request.client_origin === 'admin' ? c.origin_admin : c.origin_dynamic}
                </dd>
                <dt>{c.redirect_label}</dt>
                <dd data-testid="oauth-consent-redirect">
                  <code>{request.redirect_host}</code>
                </dd>
                <dt>{c.organisation_label}</dt>
                <dd data-testid="oauth-consent-org">{request.organisation_name}</dd>
              </dl>

              {/* Gate E12.5 pass 1 (P2-8) — for a loopback or private-use
                  redirect the host above says nothing about WHO receives the
                  approval, so the page says that, in words, before the
                  buttons. The server decides which (`redirect_warning`). */}
              {request.redirect_warning && (
                <div
                  className="dm-alert dm-alert--warning"
                  data-testid="oauth-consent-redirect-warning"
                >
                  <p>
                    <strong>{c.redirect_warning_title}</strong>
                  </p>
                  <p>
                    {formatTemplate(
                      request.redirect_warning === 'loopback'
                        ? c.redirect_warning_loopback
                        : c.redirect_warning_private_use_scheme,
                      { host: request.redirect_host },
                    )}
                  </p>
                </div>
              )}

              <h2>{c.scopes_heading}</h2>
              <ul style={listStyle} data-testid="oauth-consent-scopes">
                {request.scopes.map((scope) => (
                  <li key={scope}>{scopeText(scope)}</li>
                ))}
              </ul>
              {request.scopes.includes('mcp:write') && <p>{c.scope_write_note}</p>}

              <h2>{c.tools_heading}</h2>
              {request.tools.length === 0 ? (
                <p>{c.tools_none}</p>
              ) : (
                <ul style={listStyle} data-testid="oauth-consent-tools">
                  {request.tools.map((tool) => (
                    <li key={tool}>
                      <code>{tool}</code>
                    </li>
                  ))}
                </ul>
              )}

              <p>{c.acts_as_you}</p>

              {decideError && (
                <p className="login-error" role="alert" data-testid="oauth-consent-decide-error">
                  {decideError}
                </p>
              )}
              {locked && !decideError && (
                <p role="status" data-testid="oauth-consent-submitting">
                  {decided ? c.redirecting : c.submitting}
                </p>
              )}
              <div className="dm-button-row">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={locked}
                  aria-busy={submitting === 'approve' || undefined}
                  onClick={() => void decide('approve')}
                  data-testid="oauth-consent-approve"
                >
                  {c.approve}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={locked}
                  aria-busy={submitting === 'deny' || undefined}
                  onClick={() => void decide('deny')}
                  data-testid="oauth-consent-deny"
                >
                  {c.deny}
                </button>
              </div>
            </>
          )
        )}
      </div>
    </main>
  );
}
